import { animated, useSpring } from '@react-spring/web';
import { hooks } from '@wove/react';
import { FC, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NativeLockScroll } from '../native-lock-scroll';
import { GetContainer, resolveContainer } from '../utils/get-container';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import './style.less';

const classPrefix = `v-mask`;

const opacityRecord = {
  default: 0.55,
  thin: 0.35,
  thick: 0.75,
};

export type MaskProps = {
  visible?: boolean;
  onMaskClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  color?: 'black' | 'white';
  opacity?: 'default' | 'thin' | 'thick' | number;
  getContainer?: GetContainer;
  afterShow?: () => void;
  afterClose?: () => void;
  stopPropagation?: boolean;
} & CommonPropsWithChildren;

export const Mask: FC<MaskProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);

  const background = useMemo(() => {
    const opacity = opacityRecord[props.opacity || 'default'] ?? props.opacity;
    const rgb = props.color === 'white' ? '255, 255, 255' : '0, 0, 0';
    return `rgba(${rgb}, ${opacity})`;
  }, [props.color, props.opacity]);

  const [active, setActive] = useState(props.visible);

  const { opacity } = useSpring({
    opacity: props.visible ? 1 : 0,
    config: {
      precision: 0.01,
      mass: 1,
      tension: 200,
      friction: 30,
    },
    onStart: () => {
      setActive(true);
    },
    onRest: () => {
      setActive(props.visible);
      if (props.visible) {
        props.afterShow?.();
      } else {
        props.afterClose?.();
      }
    },
  });

  const onMaskClick = hooks.useCallbackRef((event) => {
    if (props.stopPropagation) {
      event.stopPropagation();
    }
    props.onMaskClick?.(event as React.MouseEvent<HTMLDivElement, MouseEvent>);
  });

  const node = withNativeProps(
    props,
    <NativeLockScroll>
      <animated.div
        className={classPrefix}
        ref={ref}
        style={{
          ...props.style,
          background,
          opacity,
          display: active ? 'unset' : 'none',
        }}
        onClick={onMaskClick}
      >
        {props.children && <div className={`${classPrefix}-content`}>{props.children}</div>}
      </animated.div>
    </NativeLockScroll>,
  );

  if (props.getContainer) {
    const container = resolveContainer(props.getContainer);
    return createPortal(node, container);
  } else {
    return node;
  }
};

Mask.defaultProps = {
  visible: true,
  color: 'black',
  opacity: 'default',
  getContainer: null,
  stopPropagation: true,
};
